import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { ClientPartner } from '../../../_models/client-partner';
import { ClientPartnerService } from '../../../_services/client-partner.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-client-partner-form',
    templateUrl: './client-partner-form.component.html',
    styleUrls: ['./client-partner-form.component.css'],
    standalone: false
})
export class ClientPartnerFormComponent implements OnInit {
    title = 'Add Partner';
    partnerId: string | null = null;
    loading = true;
    submitting = false;
    form: UntypedFormGroup;
    clientPartner: ClientPartner | null = null;
    logo: Blob | null = null;
    previewUrl: string | ArrayBuffer | null = null;
    existingLogoUrl = '';

    constructor(
        private titleService: Title,
        private router: Router,
        private fb: UntypedFormBuilder,
        private activatedRoute: ActivatedRoute,
        private clientPartnerService: ClientPartnerService,
        private toastr: ToastrService
    ) {
        this.partnerId = this.activatedRoute.snapshot.paramMap.get('id');
        if (!this.partnerId) {
            this.titleService.setTitle(this.title);
        } else {
            this.title = 'Edit Partner';
        }
    }

    ngOnInit() {
        this.buildForm();
        if (this.partnerId) {
            this.loadClientPartner();
        } else {
            this.loading = false;
        }
    }

    loadClientPartner() {
        this.clientPartnerService.loadOne(this.partnerId!).subscribe({
            next: (response: any) => {
                const partner = response.data || response;
                this.clientPartner = partner;
                this.existingLogoUrl = this.getFullLogoUrl(partner.logoPath);
                this.titleService.setTitle('Edit Partner');
                this.updateForm();
                this.loading = false;
            },
            error: () => {
                this.toastr.error('Failed to load partner');
                this.loading = false;
            }
        });
    }

    getFullLogoUrl(path: string): string {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${environment.apiUrl}/${path}`;
    }

    onChangeFile(event: any) {
        const file = event.target.files[0];
        if (file) {
            this.logo = file;
            const reader = new FileReader();
            reader.onload = (e: any) => {
                this.previewUrl = e.target.result;
            };
            reader.readAsDataURL(file);
        }
    }

    save() {
        if (this.form.invalid) return;

        if (!this.logo && !this.partnerId) {
            this.toastr.error('Please upload a logo');
            return;
        }

        this.submitting = true;

        const partnerData: ClientPartner = {
            id: this.partnerId || '',
            clientPartnerName: this.form.value.clientPartnerName,
            logoPath: ''
        };

        this.clientPartnerService.save(partnerData, this.logo).subscribe({
            next: () => {
                this.submitting = false;
                this.toastr.success(this.partnerId ? 'Partner updated successfully' : 'Partner added successfully');
                this.router.navigateByUrl('/admin/clients-partners');
            },
            error: () => {
                this.submitting = false;
                this.toastr.error('Failed to save partner');
            }
        });
    }

    private buildForm() {
        this.form = this.fb.group({
            clientPartnerName: new UntypedFormControl('', [Validators.required]),
            logo: new UntypedFormControl('', this.partnerId ? [] : [Validators.required])
        });
    }

    private updateForm() {
        this.form.patchValue({
            clientPartnerName: this.clientPartner?.clientPartnerName
        });
        this.form.get('logo')?.clearValidators();
    }
}
